// Ícone "?" do cabeçalho: "Como usar esta tela" (PO1-3506, épico PO1-3504).
//
// Só aparece quando a tela atual tem tutorial no catálogo (TreinamentoProvider
// + tutorialDaTela, por rota exata e `?aba=`). Abre o PainelAjuda por cima da
// tela, sem navegar. Duas variantes do MESMO componente, como o AlertasSino:
//   - "desktop": item do <Nav> da direita, ao lado do sino;
//   - "mobile": botão fixo no cabeçalho, dentro do alertas-sino-mobile-wrap.
// Cada variante só se monta na sua faixa de largura (matchMedia).
//
// Novidade: tutorial `novo` (até 30 dias) que o usuário ainda não viu mostra um
// aviso abaixo do ícone, "Novidade: veja como usar", com Ver e Dispensar. As
// duas ações gravam o "já vi" (PUT /treinamento/vistos/:codigo): uma vez por
// usuário e por tutorial. O admin Everlog não vê o aviso.
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useTreinamento } from "../../contexts/TreinamentoContext";
import { useAuth } from "../../hooks/auth";
import tutorialDaTela from "../../views/Treinamento/tutorialDaTela";
import ManualModal from "../../views/Treinamento/ManualModal";
import ChatModal from "../autopilot/ChatModal";
import PainelAjuda from "./PainelAjuda";

import "./styles.css";

const MEDIA_CELULAR = "(max-width: 991.98px)";

function lerCelular() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MEDIA_CELULAR).matches;
}

function useCelular() {
  const [celular, setCelular] = useState(lerCelular);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(MEDIA_CELULAR);
    const onChange = () => setCelular(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);
  return celular;
}

// Tutorial da tela atual (rota + aba). Também usado pelo AdminNavbar para
// reservar espaço no celular quando o ícone existe.
export function useTutorialDaTela() {
  const location = useLocation();
  const { tutoriais } = useTreinamento();
  return useMemo(
    () => tutorialDaTela(tutoriais, location.pathname, location.search),
    [tutoriais, location.pathname, location.search],
  );
}

export function mostraNovidade(tutorial, vistos, user) {
  if (!tutorial || !tutorial.novo) return false;
  if (user && user.setor === "admin") return false;
  return (vistos || []).indexOf(tutorial.codigo) === -1;
}

export default function AjudaTela({ variant = "desktop", className = "" }) {
  const location = useLocation();
  const { user } = useAuth();
  const { vistos, marcarVisto } = useTreinamento();
  const celular = useCelular();
  const visivel = variant === "mobile" ? celular : !celular;
  const achado = useTutorialDaTela();
  const [aberto, setAberto] = useState(false);
  const [dispensado, setDispensado] = useState(null);
  // Chat do suporte aberto pelo painel: mora aqui, fora do painel, que fecha.
  const [chatSuporte, setChatSuporte] = useState(null);
  // Manual aberto pelo painel (PO1-3738): também mora aqui, fora do painel.
  const [manual, setManual] = useState(null);

  // Fecha ao mudar de rota (e ao trocar de faixa de largura).
  useEffect(() => {
    setAberto(false);
  }, [location.pathname, location.search, visivel]);

  const fechar = useCallback(() => setAberto(false), []);

  if (!visivel || !achado) return null;

  const { tutorial, inicioSegundos, outros } = achado;
  const tela = location.pathname + location.search;
  const novidade = mostraNovidade(tutorial, vistos, user) && dispensado !== tutorial.codigo;

  const abrir = () => {
    setAberto(true);
    if (novidade) {
      setDispensado(tutorial.codigo);
      marcarVisto(tutorial.codigo);
    }
  };

  const dispensar = () => {
    setDispensado(tutorial.codigo);
    marcarVisto(tutorial.codigo);
  };

  const botao = (
    <button
      type="button"
      className={`ajuda-tela__botao${novidade ? " ajuda-tela__botao--novo" : ""}`}
      aria-label="Como usar esta tela"
      title="Como usar esta tela"
      aria-expanded={aberto}
      onClick={abrir}
      data-testid="ajuda-tela-botao"
    >
      <span className="ajuda-tela__icone" aria-hidden="true">
        {/* Lâmpada (dica), da mesma família do sino (tim-icons), escolha do Rodrigo. */}
        <i className="tim-icons icon-bulb-63" />
      </span>
      {variant === "desktop" ? <p className="d-lg-none" style={{ marginLeft: 6 }}>Como usar esta tela</p> : null}
    </button>
  );

  const aviso = novidade ? (
    <div className="ajuda-tela__novidade" role="status" data-testid="ajuda-tela-novidade">
      <span className="ajuda-tela__novidade-titulo">Novidade: veja como usar</span>
      <span>{tutorial.titulo}</span>
      <div className="ajuda-tela__novidade-acoes" style={{ marginTop: "0.45rem" }}>
        <button type="button" className="ajuda-tela__link" onClick={abrir}>
          Ver
        </button>
        <button type="button" className="ajuda-tela__link ajuda-tela__link--mudo" onClick={dispensar}>
          Dispensar
        </button>
      </div>
    </div>
  ) : null;

  const falarComSuporte = (contextoInicial) => {
    fechar();
    setChatSuporte(contextoInicial);
  };

  const painel = (
    <>
      {aberto ? (
        <PainelAjuda
          tutorial={tutorial}
          inicioSegundos={inicioSegundos}
          outros={outros}
          tela={tela}
          onFechar={fechar}
          onFalarComSuporte={falarComSuporte}
          onAbrirManual={setManual}
        />
      ) : null}
      {manual ? <ManualModal isOpen tutorial={manual} onClose={() => setManual(null)} /> : null}
      {chatSuporte ? (
        <ChatModal isOpen onClose={() => setChatSuporte(null)} title="Suporte Everlog" contextoInicial={chatSuporte} />
      ) : null}
    </>
  );

  if (variant === "mobile") {
    return (
      <div className={`ajuda-tela ajuda-tela--mobile ${className}`.trim()}>
        {botao}
        {aviso}
        {painel}
      </div>
    );
  }

  return (
    <li className={`nav-item ajuda-tela ${className}`.trim()}>
      {botao}
      {aviso}
      {painel}
    </li>
  );
}
